import { Button, Popover, PopoverContent, PopoverTrigger } from '@ibsys/kit';
import { PencilIcon, XIcon } from 'lucide-react';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateExpertReportRuleManualComment } from '@/entities/expert-report';
import { useTriggerTooltip } from '@/shared/lib';
import { RichTextEditor, type RichTextEditorHandle } from '@/shared/ui';
import { buildKnowledgeNodeMarker } from './commentMarkers';
import { KnowledgeNodeSuggestions } from './KnowledgeNodeSuggestions';

type Props = {
  conversationId: number;
  expertReportId: number;
  ruleId: number;
  /** Действующий комментарий правила: изменённый пользователем, иначе составленный системой */
  effectiveComment: string;
  /** Комментарий, изменённый пользователем; null — правка не сохранялась */
  manualComment: string | null;
};

/** Незавершённое обращение к узлам знаний: знак `@` и набранное после него до курсора. */
const KNOWLEDGE_NODE_QUERY = /@(\S*)$/;

/**
 * Правка комментария правила: кнопка стоит у названия правила, поле открывается
 * в поповере и сохраняется по Shift+Enter или при уходе фокуса; крестик закрывает окно без сохранения.
 * Пустое поле означает сброс — комментарий возвращается к составленному системой.
 * Знак `@` открывает список узлов знаний, выбранный узел встаёт в текст ссылкой.
 */
export const EvaluationRuleCommentEditor: FC<Props> = ({
  conversationId,
  expertReportId,
  ruleId,
  effectiveComment,
  manualComment,
}) => {
  const { t } = useTranslation(['dialogs']);
  const tooltip = useTriggerTooltip();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(effectiveComment);
  const [suggestionQuery, setSuggestionQuery] = useState<string | null>(null);
  const isCancelledRef = useRef(false);
  const editorRef = useRef<RichTextEditorHandle>(null);
  /** Позиция `@запроса` на момент набора: к клику по подсказке редактор уже потерял выделение */
  const mentionRangeRef = useRef<{ index: number; length: number } | null>(null);
  const { mutateAsync, isPending } = useUpdateExpertReportRuleManualComment();

  // Пока поповер закрыт, поле следует за пришедшим с сервера значением
  useEffect(() => {
    if (!isOpen) setInputValue(effectiveComment);
  }, [isOpen, effectiveComment]);

  const saveValue = useCallback(async () => {
    const trimmed = inputValue.trim();
    const nextManual = trimmed === '' ? null : inputValue;
    if (nextManual === manualComment) return;
    // Правка, совпавшая с комментарием системы, — не правка: сохранять её нечего
    if (nextManual !== null && manualComment === null && nextManual === effectiveComment) return;
    try {
      await mutateAsync({ conversationId, expertReportId, ruleId, manualComment: nextManual });
    } catch {
      // Ошибку показал глобальный перехватчик запросов; поле возвращаем к сохранённому значению
      setInputValue(effectiveComment);
    }
  }, [conversationId, expertReportId, ruleId, manualComment, effectiveComment, inputValue, mutateAsync]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      // Каждое открытие начинается с чистой пометки: иначе отмена прошлой правки погасила бы
      // сохранение следующей
      if (open) isCancelledRef.current = false;
      if (!open) {
        setSuggestionQuery(null);
        if (isCancelledRef.current) {
          isCancelledRef.current = false;
          setInputValue(effectiveComment);
        } else {
          void saveValue();
        }
      }
      tooltip.handleWindowOpenChange(open);
      setIsOpen(open);
    },
    [saveValue, effectiveComment, tooltip.handleWindowOpenChange],
  );

  const handleCancel = useCallback(() => {
    isCancelledRef.current = true;
    // Контролируемый Popover не зовёт onOpenChange при закрытии снаружи — закрываем общим путём:
    // иначе подсказка кнопки не узнает, что окно закрылось
    handleOpenChange(false);
  }, [handleOpenChange]);

  const handleChange = useCallback((html: string) => {
    setInputValue(html);

    // Позицию курсора читаем после того, как редактор применит правку
    requestAnimationFrame(() => {
      const editor = editorRef.current;
      const cursorIndex = editor?.getSelectionIndex();
      if (!editor || cursorIndex == null) return;

      const queryMatch = editor.getText(0, cursorIndex).match(KNOWLEDGE_NODE_QUERY);
      mentionRangeRef.current = queryMatch
        ? { index: cursorIndex - queryMatch[0].length, length: queryMatch[0].length }
        : null;
      setSuggestionQuery(queryMatch ? queryMatch[1] : null);
    });
  }, []);

  const applyKnowledgeNode = useCallback((nodeId: number) => {
    const mention = mentionRangeRef.current;
    if (!mention) return;

    // Ввод продолжается сразу за вставленной ссылкой
    editorRef.current?.replaceText(mention.index, mention.length, buildKnowledgeNodeMarker(nodeId));
    editorRef.current?.focus();
    mentionRangeRef.current = null;
    setSuggestionQuery(null);
  }, []);

  /**
   * Escape закрывает окно отменой.
   *
   * Клавишу перехватывает сам Popover, и делает это раньше поля: без этой пометки закрытие пошло бы
   * обычным путём — с сохранением, то есть Escape сохранял бы правку вместо того, чтобы отменить её.
   */
  const handleEscapeKeyDown = useCallback(() => {
    isCancelledRef.current = true;
  }, []);

  /**
   * Popover при открытии фокусирует первый элемент содержимого — кнопку тулбара, и её подсказка
   * всплывает по фокусу поверх соседнего текста. Курсор сразу ставим в поле: этого и ждёт правка.
   */
  const handleOpenAutoFocus = useCallback((e: Event) => {
    e.preventDefault();
    editorRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        // Контролируемый Popover не зовёт onOpenChange при закрытии снаружи — сохраняем явно
        handleOpenChange(false);
      }
      // Escape здесь не обрабатываем: клавишу перехватывает сам Popover, он же закрывает окно —
      // отмену помечает `handleEscapeKeyDown`
    },
    [handleOpenChange],
  );

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-5 align-middle"
          aria-label={t('dialogs:expertReport.editComment')}
          tooltip={t('dialogs:expertReport.editComment')}
          onFocus={tooltip.handleFocus}
          onPointerMove={tooltip.handlePointerMove}
          icon={<PencilIcon className="size-3" />}
        />
      </PopoverTrigger>
      <PopoverContent
        className="w-96 p-3"
        align="start"
        onOpenAutoFocus={handleOpenAutoFocus}
        onEscapeKeyDown={handleEscapeKeyDown}
      >
        <div className="flex flex-col gap-2">
          <div className="flex items-start gap-2">
            <div className="relative flex-1">
              <RichTextEditor
                ref={editorRef}
                value={inputValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                readOnly={isPending}
                // Ограничиваем высоту поля: без этого длинный комментарий растягивает popover за экран
                editorClassName="[&_.ql-editor]:max-h-48 [&_.ql-editor]:min-h-20"
              />
              {suggestionQuery !== null && (
                <KnowledgeNodeSuggestions query={suggestionQuery} onSelect={applyKnowledgeNode} />
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="size-6"
              aria-label={t('dialogs:expertReport.close')}
              icon={<XIcon className="size-3" />}
              onClick={handleCancel}
            />
          </div>
          <p className="text-xs text-muted-foreground">{t('dialogs:expertReport.commentSyntaxHint')}</p>
        </div>
      </PopoverContent>
    </Popover>
  );
};
